export const ANALYTICS_ALLOWED_EVENTS = [
  "page_view",
  "view_item",
  "begin_checkout",
] as const;
export type AnalyticsAllowedEvent = (typeof ANALYTICS_ALLOWED_EVENTS)[number];

export const ANALYTICS_FORBIDDEN_PARAM_SUBSTRINGS = [
  "email",
  "user_id",
  "userid",
  "wallet",
  "payment",
  "stripe",
  "psa",
  "vault",
  "custody",
  "support",
  "ticket",
  "reset",
  "password",
  "token",
] as const;

const EMAIL_LIKE =
  /(?:^|[\s/])[^\s@/]+@[^\s@/]+\.[^\s@/]+(?:$|[\s/])|^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

function findForbiddenParamSubstring(key: string): string | undefined {
  const lowerKey = key.toLowerCase();
  return ANALYTICS_FORBIDDEN_PARAM_SUBSTRINGS.find((forbidden) =>
    lowerKey.includes(forbidden),
  );
}

const EVENT_PARAM_KEYS: Record<AnalyticsAllowedEvent, ReadonlySet<string>> = {
  page_view: new Set(["page_path", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]),
  view_item: new Set(["currency", "value", "items"]),
  begin_checkout: new Set(["currency", "value", "items"]),
};

const ITEM_PARAM_KEYS = new Set([
  "item_id",
  "item_name",
  "item_category",
  "item_category2",
  "price",
  "quantity",
]);

function assertAllowedParams(
  params: Record<string, unknown>,
  allowedKeys: ReadonlySet<string>,
): void {
  for (const [key, value] of Object.entries(params)) {
    const forbidden = findForbiddenParamSubstring(key);
    if (forbidden) {
      throw new Error(
        `Analytics param key "${key}" contains forbidden substring "${forbidden}"`,
      );
    }
    if (!allowedKeys.has(key)) {
      throw new Error(`Analytics param "${key}" is not allowed`);
    }
    if (typeof value === "string" && isSensitiveAnalyticsValue(value)) {
      throw new Error(`Analytics param "${key}" has a sensitive value`);
    }
    if (key === "items") {
      if (!Array.isArray(value)) throw new Error('Analytics param "items" must be an array');
      for (const item of value) {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
          throw new Error("Analytics items must be objects");
        }
        assertAllowedParams(item as Record<string, unknown>, ITEM_PARAM_KEYS);
      }
    }
  }
}

/** True for email-like / @-bearing strings that must not ship to vendors. */
export function isSensitiveAnalyticsValue(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return trimmed.includes("@") || EMAIL_LIKE.test(trimmed);
}

export function assertAllowedAnalyticsEvent(
  name: string,
  params?: Record<string, unknown>,
): asserts name is AnalyticsAllowedEvent {
  if (!ANALYTICS_ALLOWED_EVENTS.includes(name as AnalyticsAllowedEvent)) {
    throw new Error(
      `Analytics event "${name}" is not allowed. Allowed events: ${ANALYTICS_ALLOWED_EVENTS.join(", ")}`,
    );
  }

  if (params) assertAllowedParams(params, EVENT_PARAM_KEYS[name as AnalyticsAllowedEvent]);
}
